import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Edit, MapPin, Briefcase, Calendar, Euro, Clock, Cigarette, Dog, Volume2, Sparkles, Mail, Link as LinkIcon, ShieldCheck, Ban, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROOMMATE_PROFILE_STYLES = {
  Abierto:     'bg-pink-100 text-pink-700 border border-pink-200',
  Equilibrado: 'bg-orange-100 text-orange-700 border border-orange-200',
  Reservado:   'bg-violet-100 text-violet-700 border border-violet-200',
};

function BioText({ bio }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = bio.length > 150;
  return (
    <div className="mt-3">
      <p className="text-gray-600" data-testid="profile-bio">
        {isLong && !expanded ? bio.slice(0, 150) + '...' : bio}
      </p>
      {isLong && (
        <button onClick={() => setExpanded(!expanded)} className="text-sm text-[hsl(144,18%,45%)] hover:underline mt-1">
          {expanded ? 'Leer menos' : 'Leer más'}
        </button>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(true);

  // Forzar refresco de datos al entrar en el perfil
  useEffect(() => {
    const doRefresh = async () => {
      await refreshUser();
      setRefreshing(false);
    };
    doRefresh();
  }, []);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        withCredentials: true,
        headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) }
      });
      localStorage.clear();
      navigate('/');
    } catch {
      alert('Error al eliminar la cuenta. Inténtalo de nuevo.');
      setDeleting(false);
    }
  };

  if (loading || refreshing) {
    return (
      <div className="min-h-screen bg-[hsl(35,25%,91%)]">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.unsplash.com/photo-1760551937527-2bc6cfe45180?w=400';
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const roommateStyle = user.roommate_profile ? ROOMMATE_PROFILE_STYLES[user.roommate_profile] : null;

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Profile Header */}
        <div className="rounded-2xl shadow-sm border border-[hsl(35,20%,85%)] overflow-hidden relative">
          <div className="absolute inset-0">
            {user.cover_photo ? (
              <img src={user.cover_photo} alt="Portada" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-[hsl(144,18%,45%)] to-[hsl(144,18%,55%)]" />
            )}
            <div className="absolute inset-0 bg-black/40" />
          </div>
          <div className="relative px-6 pt-6 pb-6">
            <div className="flex items-start justify-between">
              <img src={getPhotoUrl(user.profile_photo)} alt={user.name} className="w-28 h-28 rounded-2xl border-4 border-white object-cover shadow-lg" data-testid="profile-photo" />
              <Link to="/profile/edit">
                <Button variant="outline" className="rounded-xl bg-white/90 hover:bg-white" data-testid="edit-profile-btn">
                  <Edit className="w-4 h-4 mr-2" />Editar perfil
                </Button>
              </Link>
            </div>
            <div className="mt-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading font-bold text-2xl text-gray-900" data-testid="profile-name">
                  {user.name}{user.age ? `, ${user.age}` : ''}
                </h1>
                {user.email_verified && (
                  <span className="inline-flex items-center gap-1 bg-[hsl(144,18%,93%)] text-[hsl(144,18%,35%)] text-xs font-medium px-2 py-0.5 rounded-full">
                    <ShieldCheck className="w-3 h-3" />Verificado
                  </span>
                )}
                {roommateStyle && (
                  <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${roommateStyle}`}>
                    🏠 {user.roommate_profile}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-gray-600">
                {user.city && <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /><span>{user.city}</span></div>}
                {user.neighborhood && <div className="flex items-center gap-1 capitalize"><MapPin className="w-4 h-4" /><span>{user.neighborhood}</span></div>}
                {user.occupation && <div className="flex items-center gap-1"><Briefcase className="w-4 h-4" /><span>{user.occupation}</span></div>}
                {user.nationality && <span className="text-sm">{user.nationality}</span>}
                {user.lgbtq_friendly && <span title="LGBTQ+ Friendly">🏳️‍🌈</span>}
                {user.erasmus && <span className="text-sm">✈️ Erasmus</span>}
              </div>
              {user.bio && <BioText bio={user.bio} />}
              {user.activity_tags && user.activity_tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {user.activity_tags.map(tag => (
                    <span key={tag} className="bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium px-2.5 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Details Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Detalles de búsqueda</h2>
            <div className="space-y-3">
              {user.budget && <div className="flex items-center gap-3 text-gray-600"><Euro className="w-5 h-5 text-gray-400" /><span>Presupuesto: <strong>{user.budget}€/mes</strong></span></div>}
              {user.move_in_date && <div className="flex items-center gap-3 text-gray-600"><Calendar className="w-5 h-5 text-gray-400" /><span>Entrada: <strong>{user.move_in_date}</strong></span></div>}
              {user.stay_duration && <div className="flex items-center gap-3 text-gray-600"><Clock className="w-5 h-5 text-gray-400" /><span>Duración: <strong>{user.stay_duration}</strong></span></div>}
              {user.preferences && <div className="mt-4"><p className="text-sm text-gray-500 mb-2">Preferencias:</p><p className="text-gray-600">{user.preferences}</p></div>}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Estilo de vida</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {user.is_smoker !== undefined && <Badge variant={user.is_smoker ? "secondary" : "outline"} className="rounded-full"><Cigarette className="w-3 h-3 mr-1" />{user.is_smoker ? 'Fumador' : 'No fumador'}</Badge>}
              {user.has_pets !== undefined && <Badge variant={user.has_pets ? "secondary" : "outline"} className="rounded-full"><Dog className="w-3 h-3 mr-1" />{user.has_pets ? 'Con mascotas' : 'Sin mascotas'}</Badge>}
            </div>
            <div className="space-y-3 text-gray-600">
              {user.cleaning_habits && <div className="flex items-center gap-3"><Sparkles className="w-5 h-5 text-gray-400" /><span>Limpieza: <strong>{user.cleaning_habits}</strong></span></div>}
              {user.sociability && <div className="flex items-center gap-3"><span className="w-5 h-5 text-gray-400 text-center">😊</span><span>Sociabilidad: <strong>{user.sociability}</strong></span></div>}
              {user.noise_habits && <div className="flex items-center gap-3"><Volume2 className="w-5 h-5 text-gray-400" /><span>Ruido: <strong>{user.noise_habits}</strong></span></div>}
              {user.schedule && <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-gray-400" /><span>Horario: <strong>{user.schedule}</strong></span></div>}
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6 mt-6">
          <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Información de contacto</h2>
          <div className="space-y-3">
            {user.contact_email && <div className="flex items-center gap-3 text-gray-600"><Mail className="w-5 h-5 text-gray-400" /><span>{user.contact_email}</span></div>}
            {user.contact_link && (
              <div className="flex items-center gap-3 text-gray-600">
                <LinkIcon className="w-5 h-5 text-gray-400" />
                <a href={user.contact_link} target="_blank" rel="noopener noreferrer" className="text-[hsl(144,18%,40%)] hover:underline">{user.contact_link}</a>
              </div>
            )}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">Contacto directo: <strong translate="no">{user.allow_direct_contact ? 'Activado' : 'Solo con match mutuo'}</strong></p>
            </div>
          </div>
        </div>

        {/* Usuarios bloqueados */}
        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6 mt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-semibold text-lg text-gray-900">Usuarios bloqueados</h2>
              <p className="text-sm text-gray-500 mt-1">Gestiona los usuarios que has bloqueado</p>
            </div>
            <Link to="/blocked-users">
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(35,20%,75%)] text-[hsl(20,10%,35%)] text-sm hover:bg-[hsl(35,25%,85%)] transition-colors">
                <Ban className="w-4 h-4" />Ver lista
              </button>
            </Link>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-6 mt-6">
          <h2 className="font-heading font-semibold text-lg text-red-600 mb-2">Zona de peligro</h2>
          <p className="text-sm text-gray-500 mb-4">Esta acción es permanente e irreversible. Se eliminarán tu cuenta, matches, mensajes e intereses.</p>
          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 rounded-xl border border-red-300 text-red-600 text-sm hover:bg-red-50 transition-colors">
              Eliminar cuenta
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-medium text-red-700 mb-3">¿Estás seguro? Esta acción no se puede deshacer.</p>
              <div className="flex gap-3">
                <button onClick={handleDeleteAccount} disabled={deleting} className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {deleting ? 'Eliminando...' : 'Sí, eliminar mi cuenta'}
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} disabled={deleting} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-600 text-sm hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
